import { Box, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { GridApiPremium, GridExceljsProcessInput } from '@mui/x-data-grid-premium';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import type { Workbook } from 'exceljs';
import dayjs from 'dayjs';
import { now, vyDate } from '@vy/utils';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { useFiltersParam } from '../../../../hooks/search-params/use-filters-param';
import { useUpcomingInvoicesReport } from '../../../../hooks/requests/use-upcoming-invoices-report';
import { useSearchWithSearchParams } from '../../../../hooks/use-search-with-search-params';
import { toastFromPromise } from '../../../../utils/vy-toasts';
import { formatDate } from '../../../../utils/date';
import { REPORT_TYPES } from '../../reports-page/reports-page-types/report-type';
import { buildBillingForecastExport } from '../../reports-page/reports-page-utils/billing-forecast-report-utils/build-billing-forecast-export';
import { ReportTable } from '../../reports-components/ReportTable';
import { useBillingForecastFilterFields } from '../hooks/use-billing-forecast-filter-fields';
import { applyBillingForecastFilters } from '../utils/apply-billing-forecast-filters';
import { applyBillingForecastSearch } from '../utils/apply-billing-forecast-search';
import {
  buildBillingForecastSummary,
  buildBillingForecastBreakdownByCustomer,
} from './BillingForecastReportTable/billing-forecast-report-aggregator';
import { BillingForecastReportTable } from './BillingForecastReportTable';
import { BillingForecastReportToolbar } from './BillingForecastReportToolbar';

const FILTERS_PARAM_NAME = 'billingForecastFilters';
const EMPTY_FILTERS: FilterLineType[] = [];

export const BillingForecastReportContent: React.FC = () => {
  const { data: upcomingInvoicesReport, isLoading, isFetching, refetch } = useUpcomingInvoicesReport();
  const [searchText, setSearchText] = useSearchWithSearchParams();

  const defaultFromDate = useMemo(
    () => vyDate().subtract(11, 'month').startOf('month').toDate(),
    [],
  );
  const defaultToDate = useMemo(() => vyDate().endOf('month').toDate(), []);

  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | null>(defaultToDate);

  const reportItems = upcomingInvoicesReport.reportItems;
  const filterFields = useBillingForecastFilterFields(reportItems);
  const [rawFilters, setRawFilters] = useFiltersParam<FilterLineType>(FILTERS_PARAM_NAME, filterFields);
  const filters = rawFilters ?? EMPTY_FILTERS;

  const handleFiltersChange = useCallback(
    (nextFilters: FilterLineType[]) => setRawFilters(nextFilters),
    [setRawFilters],
  );

  const filteredReportItems = useMemo(
    () => applyBillingForecastFilters(reportItems, filters),
    [reportItems, filters],
  );

  const filteredAndSearchedItems = useMemo(
    () => applyBillingForecastSearch(filteredReportItems, searchText),
    [filteredReportItems, searchText],
  );

  const summaryRows = useMemo(
    () => buildBillingForecastSummary(filteredAndSearchedItems),
    [filteredAndSearchedItems],
  );

  const breakdownByCustomerId = useMemo(
    () => buildBillingForecastBreakdownByCustomer(filteredAndSearchedItems),
    [filteredAndSearchedItems],
  );

  const apiRef = useGridApiRef();

  const handleExportToExcel = useCallback(async () => {
    const api = apiRef.current;
    if (!api) return;

    const fileName = `Billing Forecast Report as of ${formatDate(now(), 'MMM D, YYYY')}`;

    await toastFromPromise(
      api.exportDataAsExcel({
        fileName,
        exceljsPostProcess: async ({ workbook }: GridExceljsProcessInput) => {
          buildBillingForecastExport(workbook as unknown as Workbook, {
            filteredReportItems,
            searchText,
            fromDate,
            toDate,
            filters,
            filterFields,
          });
        },
      }),
      {
        pending: 'Downloading...',
        success: 'Download complete',
        error: 'Failed to download',
      },
    );
  }, [apiRef, filteredReportItems, searchText, fromDate, toDate, filters, filterFields]);

  const handleRefresh = useCallback(async () => {
    await toastFromPromise(refetch(), {
      pending: 'Refreshing...',
      success: 'Report refreshed',
      error: 'Failed to refresh',
    });
  }, [refetch]);

  const reportGeneratedAtDate = upcomingInvoicesReport.reportDate
    ? dayjs(upcomingInvoicesReport.reportDate)
    : null;

  const lastRefreshedLabel = reportGeneratedAtDate ? (
    <Typography variant="caption" sx={{
      color: "text.secondary"
    }}>
      Last updated: {reportGeneratedAtDate.format('MMM D, YYYY [at] h:mm A')}
    </Typography>
  ) : null;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "20px"
          }}>
          Billing Forecast
        </Typography>
        {lastRefreshedLabel}
      </Box>
      <ReportTable
        title="Billing Forecast"
        hideTitle
        exportCsvFileName="Billing Forecast Report"
        reportCreationDate={new Date()}
        reportType={REPORT_TYPES.UPCOMING_INVOICES}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        onExportCsv={handleExportToExcel}
        hideMenu
      >
        <BillingForecastReportToolbar
          onSearchChange={setSearchText}
          searchValue={searchText ?? ''}
          fromDate={fromDate}
          onFromDateChange={setFromDate}
          toDate={toDate}
          onToDateChange={setToDate}
          filterFields={filterFields}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onExport={handleExportToExcel}
          isRefreshing={isFetching}
          onRefresh={handleRefresh}
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <BillingForecastReportTable
            isLoading={isLoading}
            summaryRows={summaryRows}
            breakdownByCustomerId={breakdownByCustomerId}
            reportItems={filteredAndSearchedItems}
            fromDate={fromDate}
            toDate={toDate}
            apiRef={apiRef as MutableRefObject<GridApiPremium>}
          />
        </Box>
      </ReportTable>
    </>
  );
};
